import { TrashNewIcon } from '@app/components/icons/trash-new';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React from 'react';

import type { EditedSegment } from '../utils/cuts-to-segments';
import { timeToPixel } from '../utils/time-calculations';

type TimelineSegmentProps = {
  segment: EditedSegment;
  zoom: number;
  pixelsPerSecond: number;
  isSelected?: boolean;
  /**
   * When true the edit is frozen (PROCESSING or COMPLETED) — trim handles are
   * hidden and non-interactive so the user sees no false affordance.
   * Seeking (segment click) is unaffected.
   */
  isFrozen?: boolean;
  /** Whether this segment can be deleted (false when it is the only remaining segment). */
  canDelete?: boolean;
  dragPreview?: {
    startMs?: number;
    endMs?: number;
  };
  onSelect?: (segmentId: string) => void;
  onClick?: (segmentId: string, clientX: number) => void;
  onDeleteSegment?: (segmentId: string) => void;
  onTrimHandleMouseDown?: (
    segmentId: string,
    handle: 'start' | 'end',
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
};

const getSegmentDurationMs = (
  segment: EditedSegment,
  dragPreview?: { startMs?: number; endMs?: number },
): number => {
  const startMs = dragPreview?.startMs ?? segment.originalStartTime;
  const endMs = dragPreview?.endMs ?? segment.originalEndTime;
  return Math.max(0, endMs - startMs);
};

export const TimelineSegment: React.FC<TimelineSegmentProps> = ({
  segment,
  zoom,
  pixelsPerSecond,
  isSelected = false,
  isFrozen = false,
  canDelete = true,
  dragPreview,
  onSelect,
  onClick,
  onDeleteSegment,
  onTrimHandleMouseDown,
}) => {
  const { t } = useTranslation();
  const durationMs = getSegmentDurationMs(segment, dragPreview);
  const widthPx = Math.max(
    2,
    timeToPixel(durationMs / 1000, pixelsPerSecond, zoom),
  );
  const startOffsetMs =
    (dragPreview?.startMs ?? segment.originalStartTime) -
    segment.originalStartTime;
  const startOffsetPx = timeToPixel(
    startOffsetMs / 1000,
    pixelsPerSecond,
    zoom,
  );

  const showDeletePopup = isSelected && canDelete && !isFrozen;

  return (
    // Outer wrapper: owns layout (width / margin) and is overflow-visible so
    // the delete popup can escape upward into the ruler area.
    <div
      className="relative shrink-0 h-full overflow-visible z-10"
      style={{
        width: `${widthPx}px`,
        marginLeft: `${startOffsetPx}px`,
      }}
      data-testid={`timeline-segment-${segment.id}`}
    >
      {/* Delete popup — floats above the segment bar, centred horizontally */}
      {showDeletePopup && (
        <button
          type="button"
          className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 whitespace-nowrap rounded bg-spring-neutral-b5 px-2 py-1 text-[10px] text-white ring-1 ring-spring-neutral-b0/25 hover:bg-spring-neutral-b4"
          onClick={event => {
            event.stopPropagation();
            onDeleteSegment?.(segment.id);
          }}
          data-testid={`timeline-segment-delete-${segment.id}`}
        >
          <TrashNewIcon className="w-3 h-3 shrink-0" />
          {t('content-recording.editor-v2.delete-segment')}
        </button>
      )}

      {/* Inner bar: carries all visual styles including overflow-hidden for trim handles */}
      <div
        role="button"
        tabIndex={0}
        className={cx(
          'group relative flex h-full min-w-[2px] cursor-pointer select-none items-center justify-between overflow-hidden rounded-2xl border p-1',
          'border-spring-neutral-b0/30 bg-[#73CB87]/95',
          isSelected && 'ring-1 ring-white/70 ring-inset',
        )}
        onClick={event => {
          onSelect?.(segment.id);
          onClick?.(segment.id, event.clientX);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect?.(segment.id);
            // clientX is unavailable in keyboard events — fall back to segment start
            onClick?.(segment.id, -1);
          }
        }}
      >
        <button
          type="button"
          className={cx(
            'h-3/4 w-2 cursor-ew-resize rounded-full bg-spring-neutral-base/80 transition-opacity hover:bg-spring-neutral-base',
            isFrozen
              ? 'opacity-0 pointer-events-none'
              : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
            !isFrozen && isSelected && 'opacity-100 pointer-events-auto',
          )}
          onMouseDown={event =>
            onTrimHandleMouseDown?.(segment.id, 'start', event)
          }
          aria-label={`trim-start-${segment.id}`}
        />
        <button
          type="button"
          className={cx(
            'h-3/4 w-2 cursor-ew-resize rounded-full bg-spring-neutral-base/80 transition-opacity hover:bg-spring-neutral-base',
            isFrozen
              ? 'opacity-0 pointer-events-none'
              : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
            !isFrozen && isSelected && 'opacity-100 pointer-events-auto',
          )}
          onMouseDown={event =>
            onTrimHandleMouseDown?.(segment.id, 'end', event)
          }
          aria-label={`trim-end-${segment.id}`}
        />
      </div>
    </div>
  );
};
